// src/game/menu.ts — Menü + yükleme ekranı (DESIGN §13 madde 1, ARCHITECTURE §6): karakter seçimi (11 Rig_Medium, viewer stili döner
// model — Idle_A döngüsü klip kütüphanesi gelince), bot sayısı (üst sınır 8 − insan sayısı), zorluk, kalite (auto/kademe; el ile seçim tavanı kilitler),
// kontrol ipucu + Shift/Sticky Keys uyarısı (DESIGN §2). DOM index.html #menu / #loading iskeletine kurulur; sahne Stage'in kendisidir
// (önizleme grubu stage.scene'e eklenir, çıkışta kaldırılır). Klip kütüphanesi + clipdata menü açılır açılmaz arka planda ısıtılır.
// Dar / kısa pencerede (ör. 800×450) önizleme kamerası geri çekilir ki model paneller arası boşluğa ve başlık altına sığsın (bulgu 6;
// index.html ayrıca < 900 px'te panelleri daraltır).
//
// YEREL ÇOK OYUNCU (config/players.ts sözleşmesi): P1 klavye + İLK bağlı pad'dir (tek kişilik akış aynen korunur; UiNav zaten
// ilk dolu yuvayı okur). Diğer pad'ler menüde A'ya basarak KATILIR (P2–P4), karakter ızgarasında kendi renkli imleçleriyle
// bağımsız gezer, A ile kilitler ("HAZIR"), B ile açar; kilitsizken B oyundan ayrılır. Aynı karakteri birkaç kişi seçebilir.
// Neden ayrı imleç mantığı (UiNav değil): UiNav TEK odak/tek pad için yazılmış (odak halkası, eksen satırları, DOM sırası);
// oyuncu başına bir UiNav kurmak sağ paneldeki bot/zorluk/kalite satırlarını dört kez sürerdi. Burada P2–P4 için yalnız
// "ızgarada gez + A/B" gerekiyor, o da yirmi satırlık bir tarayıcıya sığıyor.
import * as THREE from 'three';
import type { Difficulty } from '../core/types.ts';
import { CHARACTERS } from '../config/characters.ts';
import { CLIPS } from '../config/clipTable.ts';
import { MAX_LOCAL_PLAYERS, playerColor, type LocalPlayer } from '../config/players.ts';
import { PORTRAIT_URLS, assetUrl, loadCharacter, type LoadProgress } from '../view/assets.ts';
import { QUALITY, TIERS, type QualityTier, type Stage } from '../view/stage.ts';
import type { Audio } from '../view/audio.ts';
import { getClipLibrary } from './game.ts';
import type { ClipLibrary } from '../anim/clipLibrary.ts';
import { UiNav, type Dir, type UiNavItem } from '../input/uiNav.ts';
import { GRID_COLS, MENU_COL, MENU_ROWS } from './menuLayout.ts';
import { MAX_NAME_LEN, sanitizeName, saveName } from './playerName.ts';

export type QualityChoice = QualityTier | 'auto';
/** Menüden çıkan mod: normal maç ya da eğitim (docs/TUTORIAL.md — kadro oyuncu + Manken kukla). */
export type MenuMode = 'fight' | 'tutorial' | 'online';
export interface MenuChoice {
  /** P1'in karakteri. `players[0].charIdx` ile aynıdır (tek oyunculu çağrı yolları bozulmasın diye korunuyor). */
  charIdx: number;
  bots: number; difficulty: Difficulty; quality: QualityChoice; mode: MenuMode;
  /** Ağda görünecek ad. BOŞ bırakılabilir: sunucu boş adı koltuk numarasına (P1, P2…) çeviriyor. */
  name: string;
  /** Yerel oyuncular, index sırasında (P1 HER ZAMAN ilk eleman). Eğitim tek kişiliktir → yalnız P1 döner. */
  players: LocalPlayer[];
}
/** Tek kişilik varsayılan: P1 klavye (padSlot −1), kilit gerekmez (P1 onayı DÖVÜŞ düğmesinin kendisidir). */
export const DEFAULT_CHOICE: MenuChoice = {
  charIdx: 0, bots: 3, difficulty: 'normal', quality: 'auto', mode: 'fight', name: '',
  players: [{ index: 0, fighterId: -1, padSlot: -1, charIdx: 0, ready: true }],
};

const DIFFICULTIES: readonly { id: Difficulty; label: string }[] = [{ id: 'easy', label: 'Kolay' }, { id: 'normal', label: 'Normal' }, { id: 'hard', label: 'Zor' }];
/** Önizleme dönüş hızı (rad/s) ve kamera (görsel tercih). */
const PREVIEW_SPIN = 0.55;
/** Arena kadrosu tavanı (insan + bot). Bot sayacının üst sınırı buradan türer: `8 − insan`. */
const MAX_FIGHTERS = 8;


/* ── P2–P4 pad tarayıcısı sabitleri. UiNav'ınkilerin AYNISI (o modül dışa açmıyor); menüde iki farklı gezinti hissi olmasın. */
const PAD_A = 0;
const PAD_B = 1;
const PAD_DPAD_UP = 12;
const PAD_DPAD_DOWN = 13;
const PAD_DPAD_LEFT = 14;
const PAD_DPAD_RIGHT = 15;
const NAV_DEADZONE = 0.5;
const REPEAT_DELAY_MS = 380;
const REPEAT_RATE_MS = 130;
/** İmleç rozetinin köşesi (oyuncu başına farklı) — iki kişi aynı karakterdeyse rozetler üst üste binmesin. */
const BADGE_CORNER = ['tl', 'tr', 'bl', 'br'] as const;
/** Önizleme kamerası: hedef (0, 0.95, 0) — model başlığın altında kalmasın; yön (1.1, 1.35, 5.8) = eski konum (1.1, 2.3, 5.8) − hedef,
 *  taban mesafe 6.06 u (aynı kadraj). Dar/kısa pencerede mesafe büyütülür (fit). */
const PREVIEW_TARGET = { x: 0, y: 0.95, z: 0 } as const;
const PREVIEW_DIR = { x: 1.1, y: 1.35, z: 5.8 } as const;
const PREVIEW_BASE_DIST = 6.06;
/** Zemin diski yarıçapı (u). */
const PREVIEW_DISC_R = 1.7;
/** Sığdırma (bulgu 6): karakter genişliği 2.4 u (silah + dönüş payı) paneller arası boşluğun, yüksekliği 3.0 u (disk ön kenarı → baş,
 *  perspektif dâhil) başlık altındaki alanın %90'ına sığar; hedef, başlık altındaki alanın ORTASI ekran merkezine gelecek kadar yukarı
 *  kaydırılır (model başlığın altına iner); disk de boşluğa ölçeklenir (yarı saydam panellerin altına taşmasın). */
const PREVIEW_FIT_W = 2.4;
const PREVIEW_FIT_H = 3.0;
const PREVIEW_FIT_MARGIN = 0.9;

const CONTROLS_HINT = [
  'WASD hareket · Space zıpla · J / sol tık hafif · K / sağ tık ağır',
  'L / E tut-al-fırlat · Q / orta tık blok (dokun = parry) · C dodge · T taunt',
  'Esc / P duraklat · F tam ekran · I performans · F3 hitbox · Gamepad: A B X Y L1 R1, D-pad↓ taunt',
].join('\n');
const SHIFT_WARN = 'Not: Shift de dodge\'dur ama Windows\'ta 5 ardışık Shift "Yapışkan Tuşlar" penceresini açar — C tercih edin.';
/** Pad bağlıyken menüde görünen gezinti ipucu (maç içi eşleme CONTROLS_HINT'in son satırında). */
const PAD_HINT = 'Pad bağlı: sol çubuk / D-pad gezin · A seç · Start DÖVÜŞ';
/** İkinci pad varken eklenen satır: katılma kuralı görünür olsun (kimse "pad'im çalışmıyor" sanmasın). */
const PAD_HINT_JOIN = 'Diğer padler: A ile katıl · A kilitle · B kilidi aç / ayrıl';

/* ------------------------------------------------------------- saf yardımcılar (DOM'suz) */

/**
 * Karakter ızgarasında bir adım (P2–P4 imleçleri). Kenarda SARMA YOK — UiNav'ın kuralıyla aynı: "sağa" basınca satırdan
 * çıkıp bambaşka bir yere ışınlanmak şaşırtıcı olur. Son satır eksikse (11 karakter, 3 sütun) aşağı hareket kırpılır.
 */
export function gridStep(index: number, dir: Dir, cols: number, count: number): number {
  if (count <= 0 || cols <= 0) return 0;
  const i = Math.max(0, Math.min(count - 1, index | 0));
  const col = i % cols;
  if (dir === 'left') return col > 0 ? i - 1 : i;
  if (dir === 'right') return col + 1 < cols && i + 1 < count ? i + 1 : i;
  if (dir === 'up') return i - cols >= 0 ? i - cols : i;
  return i + cols < count ? i + cols : i;
}

/**
 * Bot sayacı sınırları: insan + bot ≤ 8. Tek kişilikte alt sınır 1'dir (rakipsiz maç anlamsız), iki kişiden itibaren 0
 * olabilir — insanlar birbiriyle dövüşür.
 */
export function botRange(humans: number): { min: number; max: number } {
  const min = humans >= 2 ? 0 : 1;
  return { min, max: Math.max(min, MAX_FIGHTERS - humans) };
}

/* ------------------------------------------------------------- yükleme ekranı */

/** #loading'i gösterir ve ilerlemeyi bağlar (yüzde + son istek adı). */
export function showLoading(progress: LoadProgress): void {
  const el = document.getElementById('loading');
  const fill = document.getElementById('loading-fill');
  const label = document.getElementById('loading-label');
  if (el) el.hidden = false;
  if (fill) fill.style.width = '0%';
  if (label) label.textContent = 'yükleniyor…';
  progress.onChange = (p) => {
    const pct = Math.round(p.fraction * 100);
    if (fill) fill.style.width = pct + '%';
    if (label) label.textContent = `${pct}%  ${p.done}/${p.total}  ${p.label}`;
  };
}
export function hideLoading(): void {
  const el = document.getElementById('loading');
  if (el) el.hidden = true;
}

/* ------------------------------------------------------------- menü DOM */

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

interface MenuDom {
  charButtons: HTMLButtonElement[]; botNum: HTMLElement; diffButtons: HTMLButtonElement[]; quality: HTMLSelectElement; fight: HTMLButtonElement;
  /** ÇEVRİMİÇİ düğmesi + altındaki not satırı (kanepe uyarısı ya da lobiden dönen hata) + ad kutusu. */
  online: HTMLButtonElement; note: HTMLElement; nameInput: HTMLInputElement;
  /** DÖVÜŞ'ün alt satırı: "bu bilgisayarda · botlara karşı" (tek oyunculu yolun ne olduğu okunur olsun). */
  fightSub: HTMLElement;
  /** Eğitim (Manken antrenmanı) düğmesi: DÖVÜŞ'ün hemen altında, daha sakin renkte. */
  train: HTMLButtonElement;
  /** Önizleme sığdırması için panel / başlık kutuları. */
  title: HTMLElement; left: HTMLElement; right: HTMLElement;
  /** Pad gezintisinde odak halkası satırın tamamına çizilsin (bot sayısı / zorluk eksen öğeleri). */
  botRow: HTMLElement; diffRow: HTMLElement;
  /** Pad bağlanınca gösterilen ipucu satırı. */
  padHint: HTMLElement;
  /** Yerel oyuncu listesi (katılanlar + "A'ya bas" satırları) ve "…hazır değil" uyarısı. */
  players: HTMLElement; notReady: HTMLElement;
  /** Bot başlığı: üst sınır insan sayısına göre değiştiği için metni çalışma anında güncellenir. */
  botTitle: HTMLElement;
}

function buildMenu(root: HTMLElement, state: MenuChoice, on: { char(i: number): void; bots(d: number): void; diff(d: Difficulty): void; quality(q: QualityChoice): void; fight(): void; train(): void; online(): void; name(v: string): void }): MenuDom {
  root.replaceChildren();
  const title = el('div', 'm-title');
  title.append(el('h1', undefined, 'RING OUT'), el('small', undefined, 'rakibi platformdan at · son kalan kazanır'));
  const left = el('div', 'm-panel');
  left.appendChild(el('h2', undefined, 'Karakter'));
  const grid = el('div', 'm-chars');
  const charButtons: HTMLButtonElement[] = [];
  CHARACTERS.forEach((c, i) => {
    const b = el('button', 'm-char');
    b.type = 'button';
    const pic = el('div', 'pic');
    const url = PORTRAIT_URLS[c.id];
    if (url) pic.style.backgroundImage = `url("${assetUrl(url)}")`; else pic.textContent = c.label.slice(0, 1);
    b.append(pic, el('span', undefined, c.label));
    b.addEventListener('click', () => on.char(i));
    grid.appendChild(b);
    charButtons.push(b);
  });
  left.appendChild(grid);

  const right = el('div', 'm-panel');
  // DÖVÜŞ en üstte: kısa pencerede (≤ 400 px) panel kaydırmadan da görünür kalsın (Enter de başlatır)
  const fight = el('button', 'm-fight', 'DÖVÜŞ'); fight.type = 'button';
  fight.style.marginTop = '0'; fight.style.marginBottom = '12px';
  // Alt satır: ÇEVRİMİÇİ belirir belirmez "DÖVÜŞ de çevrimiçi mi?" sorusu doğuyor — tek oyunculu yolun ne
  // olduğu düğmenin üstünde YAZILI olsun (kullanıcının isteğinin ikinci yarısı: tek oyunculuya girilebilmek).
  const fightSub = el('small', undefined, '');
  fight.appendChild(fightSub);
  fight.addEventListener('click', () => on.fight());
  right.appendChild(fight);
  // "P3 hazır değil": DÖVÜŞ'ün hemen ALTINDA — engelin nedeni düğmenin yanında dursun (panelin dibinde kaybolmasın).
  const notReady = el('div', 'm-notready');
  notReady.hidden = true;
  right.appendChild(notReady);
  // ÇEVRİMİÇİ: DÖVÜŞ ile EĞİTİM arasında, mavi ailesinde. Ad "ÇOK OYUNCULU" DEĞİL — bu menüde zaten kanepe
  // çok oyunculu var (P2–P4 pad'le katılıyor); "çevrimiçi" tam olarak eklenen şeyi adlandırıyor.
  const online = el('button', 'm-online', 'ÇEVRİMİÇİ'); online.type = 'button';
  online.append(el('small', undefined, 'başkalarıyla oyna · oda ayarları lobide'));
  online.addEventListener('click', () => on.online());
  right.appendChild(online);
  // Not satırı: kanepe uyarısı ya da lobiden hatayla dönüşte gerekçe. Boşken gizli.
  const note = el('div', 'm-note');
  note.hidden = true;
  right.appendChild(note);
  // Ad: ÇEVRİMİÇİ'nin altında, çünkü adı sormanın gerekçesi ("başkaları seni görecek") o kararın yanında.
  // UiNav ızgarasına GİRMEZ: UiNavItem'da "odaktan ayrılırken" kancası yok, odak kutuda kilitlenirdi.
  const nameRow = el('div', 'm-name');
  const nameInput = document.createElement('input');
  nameInput.type = 'text'; nameInput.maxLength = MAX_NAME_LEN; nameInput.placeholder = 'adın (isteğe bağlı)';
  nameInput.value = state.name;
  nameInput.addEventListener('input', () => on.name(nameInput.value));
  nameRow.append(el('span', undefined, 'Ad'), nameInput);
  right.appendChild(nameRow);
  // Eğitim: dövüşün ALTINDA, sarı vurgu yerine sakin bir çerçeve — ana yol hâlâ DÖVÜŞ (DESIGN §13 menü sırası korunur).
  const train = el('button', 'm-train', 'EĞİTİM'); train.type = 'button';
  train.append(el('small', undefined, 'Manken antrenmanı · baskı yok'));
  train.addEventListener('click', () => on.train());
  right.appendChild(train);
  // Oyuncu listesi: yalnız çok oyunculu durumda (2+ pad) görünür; tek kişilik menü bugünküyle birebir aynı kalsın.
  // Başlık kabın İÇİNDE: tek `hidden` ikisini birden gizlesin (iki ayrı referans taşımaya değmez).
  const players = el('div', 'm-players');
  players.hidden = true;
  right.appendChild(players);
  const botTitle = el('h2', undefined, 'Botlar (1–7)');
  right.appendChild(botTitle);
  const botRow = el('div', 'm-row');
  const minus = el('button', undefined, '−'); minus.type = 'button';
  const botNum = el('span', 'num', String(state.bots));
  const plus = el('button', undefined, '+'); plus.type = 'button';
  minus.addEventListener('click', () => on.bots(-1)); plus.addEventListener('click', () => on.bots(1));
  botRow.append(minus, botNum, plus);
  right.appendChild(botRow);
  right.appendChild(el('h2', undefined, 'Zorluk'));
  const diffRow = el('div', 'm-row');
  const diffButtons: HTMLButtonElement[] = [];
  for (const d of DIFFICULTIES) {
    const b = el('button', undefined, d.label); b.type = 'button';
    b.addEventListener('click', () => on.diff(d.id));
    diffRow.appendChild(b); diffButtons.push(b);
  }
  right.appendChild(diffRow);
  right.appendChild(el('h2', undefined, 'Kalite'));
  const qRow = el('div', 'm-row');
  const quality = el('select');
  const opt = (v: string, label: string): HTMLOptionElement => { const o = el('option', undefined, label); o.value = v; return o; };
  quality.appendChild(opt('auto', 'Otomatik'));
  for (const t of [...TIERS].reverse()) quality.appendChild(opt(t, QUALITY[t].label));
  quality.value = state.quality;
  quality.addEventListener('change', () => on.quality(quality.value as QualityChoice));
  qRow.appendChild(quality);
  right.appendChild(qRow);
  // Pad ipucu: yalnız pad bağlıyken görünür (UiNav.onPadChange doldurur). Tarayıcı pad'i ilk düğmeye basılana kadar
  // görmediği için "bir düğmeye bas" uyarısı burada değil, ipucunun kendisi bağlanınca beliriyor.
  const padHint = el('div', 'm-hint', PAD_HINT);
  padHint.style.color = '#ffd43b';
  padHint.hidden = true;
  right.appendChild(padHint);
  right.appendChild(el('div', 'm-hint', CONTROLS_HINT));
  right.appendChild(el('div', 'm-warn', SHIFT_WARN));
  root.append(title, left, el('div'), right);
  return { charButtons, botNum, diffButtons, quality, fight, fightSub, online, note, nameInput, train, title, left, right, botRow, diffRow, padHint, players, notReady, botTitle };
}

/* ------------------------------------------------------------- önizleme */

/** Döner model: zemin diski + karakter klonu + Idle_A (klip kütüphanesi gelince). */
class Preview {
  readonly group = new THREE.Group();
  private model: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private lib: ClipLibrary | null = null;
  private token = 0;
  private readonly disc: THREE.Mesh;
  private dist = PREVIEW_BASE_DIST;
  private targetY: number = PREVIEW_TARGET.y;
  constructor(readonly stage: Stage) {
    this.group.name = 'menuPreview';
    this.disc = new THREE.Mesh(new THREE.CircleGeometry(PREVIEW_DISC_R, 40), new THREE.MeshStandardMaterial({ color: 0x1a2030, roughness: 0.95 }));
    this.disc.rotation.x = -Math.PI / 2; this.disc.receiveShadow = true;
    this.group.add(this.disc);
    stage.scene.add(this.group);
    stage.setShadowExtent(4, { x: 0, y: 0, z: 0 });
    this.placeCamera();
    void getClipLibrary().then((lib) => { this.lib = lib; this.playIdle(); });
  }
  /** Kamera = hedef + yön·mesafe; hedefe bakar (hedef y sığdırmada yukarı kayabilir). */
  private placeCamera(): void {
    const d = PREVIEW_DIR, k = this.dist / Math.hypot(d.x, d.y, d.z);
    this.stage.camera.position.set(PREVIEW_TARGET.x + d.x * k, this.targetY + d.y * k, PREVIEW_TARGET.z + d.z * k);
    this.stage.camera.lookAt(PREVIEW_TARGET.x, this.targetY, PREVIEW_TARGET.z);
  }
  /** Dar / kısa pencere: kamerayı geri çeker ki model paneller arası boşluğa (gapPx) ve başlık altındaki yüksekliğe (h − titleBottomPx)
   *  sığsın; hedef düzleminde px/u = W / (2·D·tanH) → D ≥ fitW·W / (2·tanH·gap·pay). Hedef y, başlık altındaki alanın ortası ekran
   *  merkezine gelecek kadar yukarı (titleBottom/2 px). Disk boşluğa ölçeklenir. Geniş pencerede taban mesafe. */
  fit(gapPx: number, titleBottomPx: number): void {
    const cam = this.stage.camera;
    const w = this.stage.canvas.clientWidth || 1, h = this.stage.canvas.clientHeight || 1;
    const tanV = Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2), tanH = tanV * (w / h);
    const titleB = Math.max(0, Math.min(titleBottomPx, h / 2));
    const gap = Math.max(1, gapPx) * PREVIEW_FIT_MARGIN, freeH = Math.max(1, h - titleB) * PREVIEW_FIT_MARGIN;
    const dW = PREVIEW_FIT_W * w / (2 * tanH * gap);
    const dH = PREVIEW_FIT_H * h / (2 * tanV * freeH);
    this.dist = Math.max(PREVIEW_BASE_DIST, dW, dH);
    const pxPerU = h / (2 * this.dist * tanV);
    this.targetY = PREVIEW_TARGET.y + (titleB / 2) / pxPerU;
    this.placeCamera();
    const gapWorld = gap * (2 * this.dist * tanH) / w;
    this.disc.scale.setScalar(Math.min(1, gapWorld / (2 * PREVIEW_DISC_R)));
  }
  async show(charIdx: number): Promise<void> {
    const my = ++this.token;
    const def = CHARACTERS[charIdx]!;
    const model = await loadCharacter(def);
    if (my !== this.token) { disposeModel(model); return; }
    this.clearModel();
    this.model = model;
    this.group.add(model);
    this.playIdle();
  }
  private playIdle(): void {
    if (!this.model || !this.lib) return;
    this.mixer = new THREE.AnimationMixer(this.model);
    const idle = this.lib.has(CLIPS.IDLE_A) ? this.lib.get(CLIPS.IDLE_A) : null;
    if (idle) this.mixer.clipAction(idle).play();
  }
  private clearModel(): void {
    if (this.mixer && this.model) { this.mixer.stopAllAction(); this.mixer.uncacheRoot(this.model); }
    this.mixer = null;
    if (this.model) { this.group.remove(this.model); disposeModel(this.model); this.model = null; }
  }
  update(dt: number): void {
    this.group.rotation.y += dt * PREVIEW_SPIN;
    this.mixer?.update(dt);
  }
  dispose(): void {
    this.token++;
    this.clearModel();
    this.group.removeFromParent();
    this.group.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } });
  }
}
/** SkeletonUtils.clone iskeletleri örnek başına: bırakılmazsa bone DataTexture birikir (viewer dersi). */
function disposeModel(model: THREE.Object3D): void {
  model.traverse((o) => { const s = o as THREE.SkinnedMesh; if (s.isSkinnedMesh) s.skeleton?.dispose(); });
}

/* ------------------------------------------------------------- menü döngüsü */

/** Menüyü gösterir; DÖVÜŞ (ya da Enter) ile seçimi döner. Kalite seçimi anında Stage'e uygulanır. İlk jestte ses açılır. */
export function runMenu(stage: Stage, initial: MenuChoice, audio: Audio, opts: { notice?: string | null } = {}): Promise<MenuChoice> {
  const state: MenuChoice = { ...initial };
  /** Lobiden hatayla dönüldüyse gerekçe (not satırında kırmızı). Yalnız bu menü turunda görünür. */
  const notice = opts.notice ?? null;
  const root = document.getElementById('menu');
  if (!root) throw new Error('#menu yok');
  return new Promise<MenuChoice>((resolve) => {
    const preview = new Preview(stage);
    let running = true;
    let raf = 0;
    let last = -1;

    /* ── yerel çok oyuncu durumu ──────────────────────────────────────────────────────────────────────────────────
       `joined` YALNIZ P2–P4'ü tutar: P1 her zaman kadrodadır (klavye + ilk pad), "katılma" adımı yoktur ve onayı
       DÖVÜŞ düğmesinin kendisidir. Pad kimliği HAM yuvadır (navigator.getGamepads indeksi); dışa verilen `padSlot`
       config/players.ts sözleşmesinde "kaçıncı bağlı pad" demek olduğu için çıkışta sıraya çevrilir. */
    interface Joined { index: number; raw: number; charIdx: number; ready: boolean }
    interface PadNav { held: Record<Dir, boolean>; repeatAt: Record<Dir, number>; prevA: boolean; prevB: boolean }
    const joined: Joined[] = [];
    const padNav = new Map<number, PadNav>();
    /**
     * `joined` KATILMA sırasındadır: biri ayrılıp yerine başkası girince P4, P2'nin önüne geçer. Numara sırası
     * gereken HER yerde bu kopya kullanılır — yalnız kozmetik değil: `buildPlayers` bu diziyi maça veriyor ve
     * roster.ts fighterId'yi DİZİ SIRASINDAN türetiyor (insanlar önce). Sıra bozulunca kimse yanlış karakteri
     * sürmez (eşleme kendi içinde tutarlı kalır) ama dövüşçü kimlikleri ve HUD kart şeridi P1, P3, P4, P2 diye
     * karışıyordu; config/players.ts sözleşmesi ise `players`i "index sırasında" tanımlıyor. Soğuk yol: yeniden
     * çizim ve menüden çıkış (kare başına değil).
     */
    const byIndex = (): Joined[] => [...joined].sort((a, b) => a.index - b.index);
    /** P1'in pad yuvası = EN KÜÇÜK dolu yuva (UiNav.readPad ilk dolu yuvayı okur; ayrışırlarsa P1 iki imleç sürerdi). */
    let p1Raw = -1;
    /** Bağlı ama katılmamış pad sayısı (sağ paneldeki "A'ya bas" satırları). */
    let freePadCount = 0;
    /** Bağlı yuvaların imzası — pad tak/çıkarında listeyi yeniden çizmek için. */
    let padSig = '';
    const humans = (): number => 1 + joined.length;

    // Fare yolu: ses burada çalınır (pad/klavye yolu uiNav.onNavSound'dan geçer; ikisi aynı kareye düşse bile
    // Audio.play aynı adı kare başına bir kez çalar → çift tetik zararsız).
    const dom = buildMenu(root, state, {
      char: (i) => { state.charIdx = i; refresh(); void preview.show(i); audio.ui('select'); },
      bots: (d) => { addBots(d); audio.ui('move'); },
      diff: (d) => { state.difficulty = d; refresh(); audio.ui('move'); },
      quality: (q) => { state.quality = q; applyQuality(stage, q); audio.ui('move'); },
      fight: () => finish('fight'),
      train: () => finish('tutorial'),
      online: () => finish('online'),
      name: (v) => { state.name = sanitizeName(v); saveName(state.name); },
    });

    /* ── imleçler: oyuncu başına TEK element, seçili karakter düğmesinin içine taşınır (her karede DOM kurmak yerine).
       İç içe offset (inset −3px·i) + farklı köşede rozet → iki kişi aynı karakterdeyse kimin nerede olduğu okunur.
       P1'in ayrı çerçevesi yok: mevcut `.sel` sarı kenarlığı zaten P1'in imleci, yalnız rozeti kalabalıkta belirir. */
    const cursors: HTMLElement[] = [];
    for (let i = 0; i < MAX_LOCAL_PLAYERS; i++) {
      const c = el('div', i === 0 ? 'm-cur p1' : 'm-cur');
      const col = playerColor(i);
      c.style.setProperty('--pc', col.css);
      c.style.inset = `-${i * 3}px`;
      c.appendChild(el('b', BADGE_CORNER[i] ?? 'tl', col.label));
      c.hidden = true;
      cursors.push(c);
    }
    const placeCursor = (i: number, charIdx: number, ready: boolean, show: boolean): void => {
      const c = cursors[i];
      if (!c) return;
      if (!show) { c.hidden = true; c.remove(); return; }
      const host = dom.charButtons[Math.max(0, Math.min(dom.charButtons.length - 1, charIdx))];
      if (host && c.parentElement !== host) host.appendChild(c);
      c.hidden = false;
      c.classList.toggle('ready', ready);
    };
    const paintCursors = (): void => {
      placeCursor(0, state.charIdx, true, joined.length > 0);
      for (let i = 1; i < MAX_LOCAL_PLAYERS; i++) {
        const j = joined.find((x) => x.index === i);
        placeCursor(i, j ? j.charIdx : 0, j?.ready === true, j !== undefined);
      }
    };
    /** Sağ panel: katılanlar + katılmamış pad'ler için "A'ya bas" satırı. Hiç ikinci pad yoksa bölüm gizli kalır. */
    const paintRoster = (): void => {
      const waiting = Math.max(0, Math.min(MAX_LOCAL_PLAYERS - humans(), freePadCount));
      const rows: HTMLElement[] = [];
      const row = (idx: number, name: string, status: string, cls = ''): void => {
        const r = el('div', cls ? `m-pl ${cls}` : 'm-pl');
        r.style.setProperty('--pc', playerColor(idx).css);
        r.append(el('span', 'tag', playerColor(idx).label), el('span', 'nm', name), el('span', 'st', status));
        rows.push(r);
      };
      if (joined.length > 0 || waiting > 0) {
        row(0, CHARACTERS[state.charIdx]?.label ?? '—', 'sen');
        // Numaraya göre sırala: biri ayrılıp yerine başkası katılınca liste karışmasın (P4, P2'nin üstüne çıkmasın).
        for (const j of byIndex()) {
          row(j.index, CHARACTERS[j.charIdx]?.label ?? '—', j.ready ? 'HAZIR' : 'seçiyor', j.ready ? 'ready' : '');
        }
        // Katılmamış pad'lere sıradaki boş numara gösterilir; gerçek numara katılma anında verilir.
        let n = 0;
        for (let i = 1; i < MAX_LOCAL_PLAYERS && n < waiting; i++) {
          if (joined.some((j) => j.index === i)) continue;
          row(i, 'Katılmak için A’ya bas', '', 'idle');
          n++;
        }
      }
      dom.players.replaceChildren(el('h2', undefined, 'Oyuncular'), ...rows);
      dom.players.hidden = rows.length === 0;
      // Uyarı satırı da numara sırasında ("P2, P3, P4 hazır değil"); satır listesiyle aynı sırayı okusun.
      const late = byIndex().filter((j) => !j.ready).map((j) => playerColor(j.index).label);
      dom.notReady.textContent = late.length > 0 ? `${late.join(', ')} hazır değil` : '';
      dom.notReady.hidden = late.length === 0;
    };
    /** Bot sayacı: sınırlar insan sayısına bağlı (insan + bot ≤ 8); insan katılınca değer kırpılır, başlık güncellenir. */
    const addBots = (d: number): void => {
      const r = botRange(humans());
      state.bots = Math.max(r.min, Math.min(r.max, state.bots + d));
      refresh();
    };
    const refresh = (): void => {
      const r = botRange(humans());
      state.bots = Math.max(r.min, Math.min(r.max, state.bots));
      dom.botTitle.textContent = `Botlar (${r.min}–${r.max})`;
      dom.charButtons.forEach((b, i) => b.classList.toggle('sel', i === state.charIdx));
      dom.botNum.textContent = String(state.bots);
      dom.diffButtons.forEach((b, i) => b.classList.toggle('sel', DIFFICULTIES[i]!.id === state.difficulty));
      dom.quality.value = state.quality;
      paintCursors();
      paintRoster();
      const n = humans();
      // Bot sayısı 0 olabiliyor (iki insan varken alt sınır 0'a düşüyor): "+ botlar" demek yanlış olurdu.
      dom.fightSub.textContent = n > 1
        ? `bu bilgisayarda · ${n} oyuncu${state.bots > 0 ? ' + botlar' : ''}`
        : 'bu bilgisayarda · botlara karşı';
      // Kanepe oyuncusu varken ÇEVRİMİÇİ engellenmiyor (EĞİTİM de tek kişilik ve engellenmiyor; ayrıca
      // `disabled` pad yolunu kapatmıyor — uiNav activate() kapanışı doğrudan çağırıyor). Yalnız söyleniyor.
      const couch = n > 1 ? 'Ağda şimdilik tek kişi bağlanır — P2–P4 bu turda oynamaz.' : '';
      // İkisi de gösterilir: lobiden dönen hata, kanepe uyarısını yutmasın (ikisi farklı şeyler söylüyor).
      const text = [notice, couch].filter((x) => x).join('\n');
      dom.note.textContent = text;
      dom.note.hidden = text === '';
      dom.note.style.color = notice ? '#ff6b6b' : '';
    };
    const onKey = (e: KeyboardEvent): void => {
      // Ad kutusunda Enter ÇEVRİMDIŞI maç başlatmasın: uiNav, mute ve fullscreen kısayolları zaten INPUT'u
      // atlıyor; düzeltilecek tek yer burasıydı.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Odak bir DÜĞMEDEYSE (Tab ile ÇEVRİMİÇİ/EĞİTİM'e gelinmişse) tarayıcı zaten o düğmeyi tıklıyor;
      // burada ayrıca 'fight' başlatmak oyuncuyu yanlış moda sokardı.
      if (tag === 'BUTTON') return;
      if ((e.code === 'Enter' || e.key === 'Enter') && !e.ctrlKey && !e.altKey && !e.metaKey) finish('fight');
    };

    /* ── pad / klavye gezintisi (uiNav): karakter ızgarası 3 sütun, sağ panel tek sütun (col 4).
       Bot sayısı / zorluk / kalite "eksen" öğesi: sol-sağ değeri değiştirir, odak satırdan çıkmaz. */
    const QUALITY_VALUES: QualityChoice[] = ['auto', ...[...TIERS].reverse()];
    const cycle = <X,>(arr: readonly X[], cur: X, dir: -1 | 1): X => {
      const i = arr.indexOf(cur);
      return arr[(((i < 0 ? 0 : i) + dir) % arr.length + arr.length) % arr.length]!;
    };
    const nav = new UiNav({
      onStart: () => finish('fight'),                // Start → doğrudan maça
      onPadChange: (connected) => { dom.padHint.hidden = !connected; },
      // Gezinti sesleri. `running` koşulu DÖVÜŞ içindir: uiNav 'select'i aktifleştirmeden SONRA yollar, o an menü
      // kapanmıştır (finish → running = false) ve "başlat" sesi zaten çalmıştır → onay bipi üstüne binmesin.
      onNavSound: (kind) => { if (running) audio.ui(kind); },
    });
    const FIGHT_INDEX = CHARACTERS.length;
    const navItems: UiNavItem[] = [
      ...CHARACTERS.map((_, i) => ({
        el: dom.charButtons[i]!, row: Math.floor(i / GRID_COLS), col: i % GRID_COLS,
        onFocus: () => { state.charIdx = i; refresh(); void preview.show(i); },
        activate: () => nav.setFocus(FIGHT_INDEX, true),   // karakter onaylandı → DÖVÜŞ'e atla
      })),
      { el: dom.fight, row: MENU_ROWS.fight, col: MENU_COL, activate: () => finish('fight') },
      { el: dom.online, row: MENU_ROWS.online, col: MENU_COL, activate: () => finish('online') },
      { el: dom.train, row: MENU_ROWS.train, col: MENU_COL, activate: () => finish('tutorial') },
      { el: dom.botRow, row: MENU_ROWS.bots, col: MENU_COL, axis: (d) => { addBots(d); } },
      { el: dom.diffRow, row: MENU_ROWS.diff, col: MENU_COL, axis: (d) => { state.difficulty = cycle(DIFFICULTIES.map((x) => x.id), state.difficulty, d); refresh(); } },
      { el: dom.quality, row: MENU_ROWS.quality, col: MENU_COL, axis: (d) => { state.quality = cycle(QUALITY_VALUES, state.quality, d); applyQuality(stage, state.quality); refresh(); } },
    ];
    nav.setItems(navItems, state.charIdx);

    /* ── P2–P4 pad tarayıcısı ────────────────────────────────────────────────────────────────────────────────────
       Her karede: bağlı yuvaları çıkar → kopanları kadrodan düş → katılmamış pad'de A = KATIL → katılanda D-pad/çubuk
       ızgarada gezinir, A kilitler, B kilidi açar (kilitsizken B = ayrıl). P1'in pad'ine dokunulmaz: onu UiNav sürer. */
    const getPads = (): readonly (Gamepad | null)[] =>
      (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : []);
    const newPadNav = (): PadNav => ({
      held: { up: false, down: false, left: false, right: false },
      repeatAt: { up: 0, down: 0, left: 0, right: 0 }, prevA: false, prevB: false,
    });
    /** Yön: basma kenarı + tekrar zamanlaması (uiNav'ınkiyle aynı gecikme/aralık — iki gezinti aynı hissi versin). */
    const edge = (st: PadNav, dir: Dir, active: boolean, now: number, act: () => void): void => {
      if (!active) { st.held[dir] = false; st.repeatAt[dir] = 0; return; }
      if (!st.held[dir]) { st.held[dir] = true; st.repeatAt[dir] = now + REPEAT_DELAY_MS; act(); return; }
      if (now >= st.repeatAt[dir]) { st.repeatAt[dir] = now + REPEAT_RATE_MS; act(); }
    };
    /** Katılacak pad'e verilecek en küçük boş oyuncu numarası (kadro doluysa −1). */
    const freeIndex = (): number => {
      for (let i = 1; i < MAX_LOCAL_PLAYERS; i++) if (!joined.some((j) => j.index === i)) return i;
      return -1;
    };
    const scanPads = (now: number): void => {
      const pads = getPads();
      let first = -1, sig = '';
      for (let i = 0; i < pads.length; i++) if (pads[i]) { if (first < 0) first = i; sig += `${i},`; }
      let dirty = sig !== padSig;
      padSig = sig;
      p1Raw = first;
      // Kopan pad → o oyuncu düşer. P1'in yuvası katılmış bir oyuncuya aitse (P1'in pad'i koptu, sıradaki yukarı kaydı)
      // o oyuncu da bırakılır: tek pad hem P1 imlecini hem kendi imlecini süremez.
      for (let k = joined.length - 1; k >= 0; k--) {
        const j = joined[k]!;
        if (pads[j.raw] && j.raw !== p1Raw) continue;
        joined.splice(k, 1); padNav.delete(j.raw); dirty = true;
      }
      let free = 0;
      for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (!p || i === p1Raw) continue;
        let st = padNav.get(i);
        if (!st) { st = newPadNav(); padNav.set(i, st); }
        const btn = p.buttons;
        const down = (n: number): boolean => { const x = btn[n]; return x !== undefined && (x.pressed || x.value > 0.5); };
        const a = down(PAD_A), b = down(PAD_B);
        const pressA = a && !st.prevA, pressB = b && !st.prevB;
        st.prevA = a; st.prevB = b;
        const me = joined.find((j) => j.raw === i);
        if (!me) {
          const slot = freeIndex();
          // Katılma: kadroda yer varsa A yeni oyuncu açar (imleci P1'in seçtiği karakterden başlar — göz oradadır).
          if (pressA && slot > 0) { joined.push({ index: slot, raw: i, charIdx: state.charIdx, ready: false }); audio.ui('select'); dirty = true; }
          else free++;
          continue;
        }
        if (pressA && !me.ready) { me.ready = true; audio.ui('select'); dirty = true; }
        else if (pressB && me.ready) { me.ready = false; audio.ui('back'); dirty = true; }
        else if (pressB) {                                   // kilitsizken B = oyundan ayrıl
          joined.splice(joined.indexOf(me), 1); padNav.delete(i); audio.ui('back'); dirty = true; free++;
          continue;
        }
        if (me.ready) { st.held.up = st.held.down = st.held.left = st.held.right = false; continue; }   // kilitliyken gezinmez
        const ax = p.axes[0] ?? 0, ay = p.axes[1] ?? 0;
        const step = (dir: Dir): void => {
          const next = gridStep(me.charIdx, dir, GRID_COLS, CHARACTERS.length);
          if (next === me.charIdx) return;                   // kenarda: ses de yok (uiNav kuralı — "duvara çarptım" duyulsun)
          me.charIdx = next; audio.ui('move'); dirty = true;
        };
        edge(st, 'up', down(PAD_DPAD_UP) || ay < -NAV_DEADZONE, now, () => step('up'));
        edge(st, 'down', down(PAD_DPAD_DOWN) || ay > NAV_DEADZONE, now, () => step('down'));
        edge(st, 'left', down(PAD_DPAD_LEFT) || ax < -NAV_DEADZONE, now, () => step('left'));
        edge(st, 'right', down(PAD_DPAD_RIGHT) || ax > NAV_DEADZONE, now, () => step('right'));
      }
      for (const key of [...padNav.keys()]) if (!pads[key]) padNav.delete(key);   // kopan pad durumu birikmesin
      if (free !== freePadCount) { freePadCount = free; dirty = true; }
      // İpucu satırı: ikinci pad varsa katılma kuralı da yazılsın (kimse "pad'im çalışmıyor" sanmasın).
      dom.padHint.textContent = freePadCount > 0 || joined.length > 0 ? `${PAD_HINT}\n${PAD_HINT_JOIN}` : PAD_HINT;
      if (dirty) refresh();
    };

    // Tarayıcı AudioContext'i yalnız kullanıcı jestinde açar; klavye de jest sayılır → pad/klavye ile gezen oyuncu
    // fareye dokunmadan ses duyabilsin (pad girdisi jest SAYILMAZ; ilk ses o durumda sessiz kalır, beklenen).
    const onGesture = (): void => { audio.unlock(); };
    /** Paneller arası boşluk ve başlık alt kenarı → önizleme sığdırması (getBoundingClientRect yalnız açılış + resize'da). */
    const fitPreview = (): void => {
      const l = dom.left.getBoundingClientRect(), r = dom.right.getBoundingClientRect(), t = dom.title.getBoundingClientRect();
      preview.fit(r.left - l.right, t.bottom);
    };
    /** Ham yuva → "kaçıncı bağlı pad": config/players.ts `padSlot`ı ham getGamepads indeksi DEĞİL, bağlı pad SIRASIDIR. */
    const padOrdinal = (raw: number): number => {
      if (raw < 0) return -1;
      const pads = getPads();
      let n = 0;
      for (let i = 0; i < pads.length; i++) { if (!pads[i]) continue; if (i === raw) return n; n++; }
      return -1;
    };
    /** Çıkan kadro: P1 + katılanlar (index sırasında). Eğitim tek kişiliktir (TUTORIAL.md) → yalnız P1. */
    const buildPlayers = (mode: MenuMode): LocalPlayer[] => {
      const p1: LocalPlayer = { index: 0, fighterId: -1, padSlot: padOrdinal(p1Raw), charIdx: state.charIdx, ready: true };
      if (mode === 'tutorial') return [p1];
      // byIndex(): sözleşme `players`i index sırasında ister ve roster.ts fighterId'yi dizi sırasından türetir
      // (bkz. byIndex yorumu) — katılma sırası verilirse dövüşçü kimlikleri ve HUD şeridi P1, P3, P4, P2 olur.
      const rest: LocalPlayer[] = byIndex()
        .map((j) => ({ index: j.index, fighterId: -1, padSlot: padOrdinal(j.raw), charIdx: j.charIdx, ready: true }))
        .filter((p) => p.padSlot >= 0);      // son karede kopan pad kadroya girmesin
      return [p1, ...rest];
    };
    /** Menüden çıkış: hangi düğmeyle çıkıldıysa MOD onunla yazılır (Enter / Start = DÖVÜŞ). */
    const finish = (mode: MenuMode): void => {
      if (!running) return;
      // Katılan herkes hazır değilse maç başlamaz; uyarı satırı zaten kimin beklendiğini yazıyor (DÖVÜŞ'ün altında).
      if (mode === 'fight' && joined.some((j) => !j.ready)) { audio.ui('back'); refresh(); return; }
      state.mode = mode;
      audio.ui('start');        // "DÖVÜŞ" / "EĞİTİM" onayı: tık / Enter / pad A / Start — hepsi buradan geçer
      running = false;
      cancelAnimationFrame(raf);
      removeEventListener('keydown', onKey);
      removeEventListener('keydown', onGesture);
      removeEventListener('resize', fitPreview);
      root.removeEventListener('pointerdown', onGesture);
      nav.dispose();
      root.hidden = true;
      preview.dispose();
      resolve({ ...state, players: buildPlayers(mode) });
    };
    const loop = (now: number): void => {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      const dt = last < 0 ? 0 : Math.min((now - last) / 1000, 0.1);
      last = now;
      audio.beginFrame();       // kare başı sıfırlama: aynı ad (ör. ui_move) bir sonraki karede yeniden çalabilsin
      nav.update(now);          // P1: pad okuma + tekrar zamanlaması (gerçek zaman, sim değil)
      scanPads(now);            // P2–P4: katılma + kendi imleçleri (nav'dan SONRA — aynı karede P1'in yuvası bilinsin)
      preview.update(dt);
      stage.render();
    };
    /** Maç sonrası menüye dönüş: pad'i hâlâ bağlı olan eski oyuncular yerini korur — kanepede herkes yeniden A'ya
     *  basmasın. Eşleme `padSlot` (bağlı pad sırası) üzerinden; pad başka yuvaya taşındıysa en kötü ihtimalle
     *  oyuncu düşer ve yeniden katılır. */
    const restorePlayers = (): void => {
      const pads = getPads();
      const raws: number[] = [];
      for (let i = 0; i < pads.length; i++) if (pads[i]) raws.push(i);
      p1Raw = raws[0] ?? -1;
      for (const p of initial.players ?? []) {
        if (p.index <= 0 || p.index >= MAX_LOCAL_PLAYERS || p.padSlot < 0) continue;
        const raw = raws[p.padSlot];
        if (raw === undefined || raw === p1Raw) continue;
        if (joined.some((j) => j.raw === raw || j.index === p.index)) continue;
        joined.push({ index: p.index, raw, charIdx: Math.max(0, Math.min(CHARACTERS.length - 1, p.charIdx)), ready: true });
      }
    };
    applyQuality(stage, state.quality);
    restorePlayers();
    refresh();
    void preview.show(state.charIdx);
    root.hidden = false;
    fitPreview();   // menü görünür → yerleşim hazır
    addEventListener('keydown', onGesture);   // onKey'den ÖNCE: Enter ilk tuşsa ses kilidi finish'ten önce açılsın
    addEventListener('keydown', onKey);
    addEventListener('resize', fitPreview);
    root.addEventListener('pointerdown', onGesture);
    raf = requestAnimationFrame(loop);
  });
}

/** Menü kalite seçimi → Stage: 'auto' serbest uyarlama; kademe el ile → tavan kilitli (ARCHITECTURE §10). */
export function applyQuality(stage: Stage, q: QualityChoice): void {
  if (q === 'auto') { stage.setAutoQuality(true); return; }
  stage.setQuality(q, true);
}
